import { useTranslation } from 'react-i18next'
import { FormikContextType, useFormikContext } from 'formik'
import {
  Stack,
  Text,
  Box,
  Columns,
  Column,
  TooltipRenderer,
  IconInfoInverted,
  RadioBarItem
} from '@machobear/design-system'
import { FieldInput, Dropdown, FieldRadioBar, TextArea } from '@/components/ds-form'

import { FormSection } from '../../components'
import { DetailsFormValues } from '../../types'

const ParkingStorageFields = () => {
  const { t } = useTranslation()
  const { values, setFieldValue }: FormikContextType<DetailsFormValues> =
    useFormikContext<DetailsFormValues>()

  return (
    <Stack space="xlarge">
      <FormSection
        id="parking"
        label={t('Parking availability', 'Parking availability')}
        tooltip={
          <TooltipRenderer
            id="parking-tooltip"
            placement="top"
            tooltip={
              <Text size="small">
                {t(
                  'l.listing.edit.parking_storage_info.tooltip',
                  'This information will be automatically added to digital leases created on liv.rent.'
                )}
              </Text>
            }
          >
            {({ triggerProps }) => (
              <Box aria-label="Parking tooltip" {...triggerProps}>
                <IconInfoInverted tone="secondary" size="fill" />
              </Box>
            )}
          </TooltipRenderer>
        }
      >
        <Stack space="gutter" align="left">
          <FieldRadioBar
            label=""
            required
            aria-labelledby="parking"
            name="parkingType"
            onChange={(value: string) => {
              if (value === 'none') {
                setFieldValue('parking', {})
              } else if (value === 'included') {
                setFieldValue('parking.enabled', true)
                setFieldValue('parking.hasFee', false)
              } else if (value === 'paid') {
                setFieldValue('parking.enabled', true)
                setFieldValue('parking.hasFee', true)
              }
              setFieldValue('parkingType', value)
            }}
          >
            <RadioBarItem value="none">{t('No Parking', 'No Parking')}</RadioBarItem>
            <RadioBarItem value="included">
              {t('Parking Included', 'Parking Included')}
            </RadioBarItem>
            <RadioBarItem value="paid">{t('Paid Parking', 'Paid Parking')}</RadioBarItem>
          </FieldRadioBar>
          {values?.parking?.hasFee && (
            <Columns space="gutter" collapseBelow="tablet">
              <Column width="1/2">
                <FieldInput
                  name="parking.fee"
                  prefix="$"
                  label={t('l.listing.edit.parking_fee.label', 'Parking fee')}
                  type="number"
                  inputMode="decimal"
                  required
                />
              </Column>
              <Column width="1/2">
                <Dropdown
                  name="parking.fee_frequency_txt_id"
                  label={t('l.listing.edit.payment_frequency.label', 'Payment frequency')}
                  aria-labelledby="parking.fee_frequency_txt_id"
                  required
                  options={[
                    {
                      value: 'weekly',
                      label: t('l.listing.edit.payment_frequency_weekly.text', 'Weekly')
                    },
                    {
                      value: 'monthly',
                      label: t('l.listing.edit.payment_frequency_monthly.text', 'Monthly')
                    },
                    {
                      value: 'one_time',
                      label: t('l.listing.edit.payment_frequency_one_time.text', 'One-time')
                    }
                  ]}
                />
              </Column>
            </Columns>
          )}
          {values?.parking?.enabled && (
            <Columns space="gutter" collapseBelow="tablet">
              <Column>
                <TextArea
                  name="parking.name"
                  label={t(
                    'l.listing.edit.additional_info.placeholder',
                    'Any additional information, conditions, or payment terms'
                  )}
                  lines={4}
                  lineLimit={4}
                  characterLimit={100}
                  onChange={e => setFieldValue('parking.name', e.target.value)}
                  resize={false}
                />
              </Column>
            </Columns>
          )}
        </Stack>
      </FormSection>
      <FormSection
        id="storage"
        label={t('Storage availability', 'Storage availability')}
        tooltip={
          <TooltipRenderer
            id="storage-tooltip"
            placement="top"
            tooltip={
              <Text size="small">
                {t(
                  'l.listing.edit.parking_storage_info.tooltip',
                  'This information will be automatically added to digital leases created on liv.rent.'
                )}
              </Text>
            }
          >
            {({ triggerProps }) => (
              <Box aria-label="Storage tooltip" {...triggerProps}>
                <IconInfoInverted tone="secondary" size="fill" />
              </Box>
            )}
          </TooltipRenderer>
        }
      >
        <Stack space="gutter" align="left">
          <FieldRadioBar
            label=""
            required
            aria-labelledby="storage"
            name="storageType"
            onChange={(value: string) => {
              if (value === 'none') {
                setFieldValue('storage', {})
              } else if (value === 'included') {
                setFieldValue('storage.enabled', true)
                setFieldValue('storage.hasFee', false)
              } else if (value === 'paid') {
                setFieldValue('storage.enabled', true)
                setFieldValue('storage.hasFee', true)
              }
              setFieldValue('storageType', value)
            }}
          >
            <RadioBarItem value="none">{t('No Storage', 'No Storage')}</RadioBarItem>
            <RadioBarItem value="included">
              {t('Storage Included', 'Storage Included')}
            </RadioBarItem>
            <RadioBarItem value="paid">{t('Paid Storage', 'Paid Storage')}</RadioBarItem>
          </FieldRadioBar>
          {values?.storage?.hasFee && (
            <Columns space="gutter" collapseBelow="tablet">
              <Column width="1/2">
                <FieldInput
                  name="storage.fee"
                  prefix="$"
                  label={t('l.listing.edit.storage_fee.label', 'Storage fee')}
                  type="number"
                  inputMode="decimal"
                  required
                />
              </Column>
              <Column width="1/2">
                <Dropdown
                  name="storage.fee_frequency_txt_id"
                  label={t('l.listing.edit.payment_frequency.label', 'Payment frequency')}
                  aria-labelledby="storage.fee_frequency_txt_id"
                  required
                  options={[
                    {
                      value: 'weekly',
                      label: t('l.listing.edit.payment_frequency_weekly.text', 'Weekly')
                    },
                    {
                      value: 'monthly',
                      label: t('l.listing.edit.payment_frequency_monthly.text', 'Monthly')
                    },
                    {
                      value: 'one_time',
                      label: t('l.listing.edit.payment_frequency_one_time.text', 'One-time')
                    }
                  ]}
                />
              </Column>
            </Columns>
          )}
          {values?.storage?.enabled && (
            <Columns space="gutter" collapseBelow="tablet">
              <Column>
                <TextArea
                  name="storage.name"
                  label={t(
                    'l.listing.edit.additional_info.placeholder',
                    'Any additional information, conditions, or payment terms'
                  )}
                  lines={4}
                  lineLimit={4}
                  characterLimit={100}
                  onChange={e => setFieldValue('storage.name', e.target.value)}
                  resize={false}
                />
              </Column>
            </Columns>
          )}
        </Stack>
      </FormSection>
    </Stack>
  )
}

export default ParkingStorageFields
